<template>
  <div class="py-12 bg-white">
    <div class="max-w-6xl mx-auto px-4">
      <div class="text-center mb-8">
        <h2 class="text-3xl font-bold mb-2">
          {{ content.title }}
        </h2>
        <p
          v-if="content.subtitle"
          class="text-gray-600"
        >
          {{ content.subtitle }}
        </p>
      </div>

      <div class="flex flex-col md:flex-row gap-6">
        <!-- Region sidebar -->
        <div
          class="flex md:flex-col gap-2 md:w-56 flex-shrink-0 overflow-x-auto md:overflow-x-visible pb-2 md:pb-0 hide-scrollbar"
        >
          <button
            v-for="(region, i) in regions"
            :key="region.key || i"
            class="flex items-center justify-between px-4 py-2.5 rounded-full text-sm font-medium text-left transition-colors whitespace-nowrap min-w-[160px]"
            :class="
              activeRegionIndex === i
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            "
            @click="selectRegion(i)"
          >
            <span>{{ region.name }}</span>
            <span
              v-if="region.count"
              class="ml-3 px-2 py-0.5 rounded-full text-xs font-semibold"
              :class="
                activeRegionIndex === i
                  ? 'bg-white text-primary'
                  : 'bg-white text-primary'
              "
            >
              {{ region.count }}
            </span>
          </button>
        </div>

        <!-- Cities + cards column -->
        <div class="flex-1 min-w-0">
          <!-- City tabs -->
          <div
            v-if="visibleCities.length"
            class="flex gap-6 border-b border-gray-200 mb-6 overflow-x-auto hide-scrollbar"
          >
            <button
              v-for="(city, i) in visibleCities"
              :key="city.key || i"
              class="flex items-center gap-1.5 pb-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors"
              :class="
                activeCityIndex === i
                  ? 'text-primary border-primary'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              "
              @click="selectCity(i)"
            >
              <span>{{ city.name }}</span>
              <span
                v-if="city.info"
                :title="city.info"
                class="inline-flex items-center justify-center w-4 h-4 rounded-full border border-current text-[10px] font-semibold italic"
              >
                i
              </span>
            </button>
          </div>

          <!-- Cards carousel -->
          <div
            v-if="visibleProfessions.length"
            class="relative"
          >
            <div
              ref="scrollContainer"
              class="flex gap-4 overflow-x-auto pb-4 scroll-smooth hide-scrollbar"
            >
              <div
                v-for="(prof, i) in visibleProfessions"
                :key="i"
                class="min-w-[240px] w-[calc(25%-12px)] flex-shrink-0 border border-gray-200 rounded-lg overflow-hidden flex flex-col"
              >
                <div class="bg-gray-200 aspect-[4/3] flex items-center justify-center overflow-hidden">
                  <img
                    v-if="prof.imageUrl"
                    v-lazy-load :data-src="prof.imageUrl"
                    :alt="prof.title"
                    class="w-full h-full object-cover"
                  />
                  <IconComponent
                    v-else
                    icon="wrench"
                    class="text-4xl text-gray-400"
                  />
                </div>
                <div class="p-4 flex-1 flex flex-col">
                  <h3 class="text-lg font-bold mb-2 text-gray-900">
                    {{ prof.title }}
                  </h3>
                  <p class="text-sm text-gray-600 mb-4 line-clamp-3">
                    {{ prof.description }}
                  </p>
                  <div class="mt-auto">
                    <JoszakiButton
                      :href="prof.url || null"
                      size="sm"
                      type="info"
                    >
                      {{ activeCity ? `${activeCity.name} ${prof.title}` : prof.title }}
                    </JoszakiButton>
                  </div>
                </div>
              </div>
            </div>

            <!-- Navigation -->
            <div class="flex items-center gap-2 mt-4">
              <button
                class="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-gray-400 hover:text-gray-600"
                @click="scroll(-1)"
              >
                <IconComponent icon="arrow-left" class="w-4" />
              </button>
              <button
                class="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/90"
                @click="scroll(1)"
              >
                <IconComponent icon="arrow-right" class="w-4" />
              </button>
              <div class="flex-1 h-0.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  class="h-full bg-primary rounded-full transition-all"
                  :style="{ width: scrollProgress + '%' }"
                />
              </div>
            </div>
          </div>

          <div
            v-else
            class="text-center py-12 text-gray-400"
          >
            <p>{{ $t("landing.expertsNearYou.empty") }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
  useContext,
} from "@nuxtjs/composition-api";
import useModuleContent from "~/composables/useModuleContent";

export default {
  name: "ExpertsNearYouModule",
  props: {
    moduleConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const { $posthog } = useContext();
    const { content } = useModuleContent(props);

    const regions = computed(() => content.value.regions || []);
    const activeRegionIndex = ref(0);
    const activeCityIndex = ref(0);

    const activeRegion = computed(
      () => regions.value[activeRegionIndex.value] || null
    );

    const visibleCities = computed(() => {
      const regionKey = activeRegion.value?.key;
      if (!regionKey) return [];
      return (content.value.cities || []).filter(
        (c) => c.regionKey === regionKey
      );
    });

    const activeCity = computed(
      () => visibleCities.value[activeCityIndex.value] || null
    );

    const visibleProfessions = computed(() => {
      const cityKey = activeCity.value?.key;
      if (!cityKey) return [];
      return (content.value.professions || []).filter(
        (p) => p.cityKey === cityKey
      );
    });

    const scrollContainer = ref(null);
    const scrollProgress = ref(0);

    const updateProgress = () => {
      const el = scrollContainer.value;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) {
        scrollProgress.value = 100;
        return;
      }
      scrollProgress.value = (el.scrollLeft / max) * 100;
    };

    const scroll = (dir) => {
      const el = scrollContainer.value;
      if (!el) return;
      const step = el.clientWidth * 0.8;
      el.scrollBy({ left: dir * step, behavior: "smooth" });
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateProgress();
        ticking = false;
      });
    };

    const selectRegion = (index) => {
      activeRegionIndex.value = index;
      activeCityIndex.value = 0;
    };

    const selectCity = (index) => {
      activeCityIndex.value = index;
    };

    watch(visibleProfessions, () => {
      nextTick(() => {
        if (scrollContainer.value) scrollContainer.value.scrollLeft = 0;
        updateProgress();
      });
    });

    onMounted(() => {
      const defaultRegion = parseInt(content.value.defaultRegion, 10);
      if (!isNaN(defaultRegion) && regions.value[defaultRegion]) {
        activeRegionIndex.value = defaultRegion;
      }

      nextTick(() => {
        if (scrollContainer.value) {
          scrollContainer.value.addEventListener("scroll", onScroll);
          updateProgress();
        }
      });

      $posthog?.capture("front_page_module_viewed", {
        module_slug: props.moduleConfig.slug,
        module_position: props.moduleConfig.position,
        module_row: props.moduleConfig.row,
        version_id: props.moduleConfig.versionId,
      });
    });

    onBeforeUnmount(() => {
      if (scrollContainer.value) {
        scrollContainer.value.removeEventListener("scroll", onScroll);
      }
    });

    return {
      content,
      regions,
      activeRegionIndex,
      activeCityIndex,
      visibleCities,
      activeCity,
      visibleProfessions,
      scrollContainer,
      scrollProgress,
      selectRegion,
      selectCity,
      scroll,
    };
  },
};
</script>

<style scoped>
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
